import {
  getEvidenceStatusLabel,
  isAnalyzingStatus,
  isApprovedStatus,
  isAwaitingReviewStatus,
  isRejectedStatus,
} from "@cle/models/evidenceItem";
import ClePagination from "./ClePagination";
import CleTable, { type CleTableColumn } from "./CleTable";
import { CourseCertificateEvidenceListModel } from "../models/courseCertificate";
import { formatLessons } from "../utils/lessonLimits";

type CourseCertificatesTableProps = {
  certificates: CourseCertificateEvidenceListModel[];
  isError?: boolean;
  isLoading?: boolean;
  onCertificateClick?: (certificateId: string) => void;
  pagination?: {
    isDisabled?: boolean;
    onPageChange: (page: number) => void;
    page: number;
    total: number;
    totalPages: number;
  };
  showStatusColumn?: boolean;
};

const MAX_FILE_NAME_LENGTH = 30;

const truncateFileName = (fileName: string): string => {
  if (fileName.length <= MAX_FILE_NAME_LENGTH) {
    return fileName;
  }

  return `${fileName.slice(0, MAX_FILE_NAME_LENGTH)}...`;
};

const getStatusBadgeClass = (
  certificate: CourseCertificateEvidenceListModel,
) => {
  if (certificate.isReported) {
    return "cle-status-badge--reported";
  }

  if (isApprovedStatus(certificate.status)) {
    return "cle-status-badge--success";
  }

  if (isAwaitingReviewStatus(certificate.status)) {
    return "cle-status-badge--warning";
  }

  if (isRejectedStatus(certificate.status)) {
    return "cle-status-badge--danger";
  }
  if (certificate.status === "failed") {
    return "cle-status-badge--danger";
  }

  return "cle-status-badge--info";
};

const renderAnalyzingPlaceholder = (className: string) => (
  <span className={`placeholder rounded ${className}`} />
);

const CourseCertificatesTable = ({
  certificates,
  isError = false,
  isLoading = false,
  onCertificateClick,
  pagination,
  showStatusColumn = true,
}: CourseCertificatesTableProps) => {
  const columns: CleTableColumn<CourseCertificateEvidenceListModel>[] = [
    {
      id: "fileName",
      header: "Filnavn",
      title: (certificate) => certificate.fileName ?? "",
      cell: (certificate) => truncateFileName(certificate.fileName ?? ""),
    },
    {
      id: "title",
      header: "Titel",
      cell: (certificate) =>
        isAnalyzingStatus(certificate.status)
          ? renderAnalyzingPlaceholder("col-7")
          : certificate.courseTitle,
    },
    {
      id: "provider",
      header: "Udbyder",
      cell: (certificate) =>
        isAnalyzingStatus(certificate.status)
          ? renderAnalyzingPlaceholder("col-6")
          : certificate.courseProvider,
    },
    {
      id: "lessons",
      header: "Lektioner",
      cell: (certificate) =>
        isAnalyzingStatus(certificate.status)
          ? renderAnalyzingPlaceholder("col-4")
          : formatLessons(certificate.numberOfLessons),
    },
    {
      id: "date",
      header: "Dato",
      cell: (certificate) =>
        isAnalyzingStatus(certificate.status)
          ? renderAnalyzingPlaceholder("col-6")
          : certificate.courseDate,
    },
    ...(showStatusColumn
      ? [
          {
            id: "status",
            header: "Status",
            cell: (certificate: CourseCertificateEvidenceListModel) => {
              const isAnalyzing = isAnalyzingStatus(certificate.status);

              return (
                <span
                  style={{ fontSize: "0.75rem" }}
                  className={`cle-status-badge  gap-2 ${getStatusBadgeClass(
                    certificate,
                  )}`}
                >
                  {isAnalyzing ? (
                    <span
                      className="spinner-border spinner-border-sm"
                      aria-hidden="true"
                    />
                  ) : null}
                  {getEvidenceStatusLabel(certificate.status)}
                </span>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <>
      <CleTable
        columns={columns}
        isError={isError}
        isLoading={isLoading}
        errorMessage="Kursusbeviser kunne ikke indlæses lige nu."
        emptyTitle="Ingen kursusbeviser uploadet endnu"
        emptyDescription="Når du uploader et kursusbevis, vises det her i oversigten."
        data={certificates}
        getRowId={(certificate) => certificate.id}
        getRowProps={(certificate) => ({
          "aria-busy": isAnalyzingStatus(certificate.status) ? true : undefined,
          className: isAnalyzingStatus(certificate.status)
            ? "placeholder-glow"
            : undefined,
        })}
        rowAction={
          onCertificateClick
            ? {
                className: "",
                onClick: (certificateId) => onCertificateClick(certificateId),
              }
            : undefined
        }
      />
      {pagination != null && certificates.length > 0 ? (
        <ClePagination
          ariaLabel="Kursusbeviser pagination"
          isDisabled={pagination.isDisabled}
          itemLabel="kursusbeviser"
          onPageChange={pagination.onPageChange}
          page={pagination.page}
          total={pagination.total}
          totalPages={pagination.totalPages}
        />
      ) : null}
    </>
  );
};

export default CourseCertificatesTable;
